import { WINDOWS_ARM_REINSTALL } from '@api/devices/agent-platform';
import type { DeviceFacts, DeviceSignal, DeviceTelemetry, RunbookSummary } from '@api/devices/contracts';
import { antivirusProductState } from '@api/devices/antivirus';
import { Activity, ArrowUpCircle, CalendarClock, CircleCheck, Cpu, HardDrive, Info, MemoryStick, Play, ShieldAlert, ShieldCheck, TriangleAlert, Wrench } from 'lucide-react';
import type { ReactNode } from 'react';
import { absolute, ago, duration } from '../lib/time';
import { formatBytes } from '../lib/format';
import { Sparkline, UsageBar } from '../ui/charts';
import { cn } from '../ui/cn';
import { Card, useNow } from '../ui/misc';
import { describePresence } from './presence';

const evidenceLabel: Record<string, string> = {
  mount: 'Volume', freeBytes: 'Free', totalBytes: 'Size', freePct: 'Free %', daysToFull: 'Full in (days)', bytesPerDay: 'Change per day',
  averagePct: 'Average %', samples: 'Samples', memoryBytes: 'Installed', since: 'Since', pendingCount: 'Waiting updates',
  lastInstalledAt: 'Last installed', signaturesUpdatedAt: 'Definitions from', label: 'Release', endsAt: 'Support ends', diskEncryption: 'Encryption',
  chassis: 'Type', bootAt: 'Booted', system: 'System volume',
};
function formatEvidence(key: string, value: unknown) {
  if (typeof value === 'number' && /Bytes|PerDay/.test(key)) return formatBytes(Math.abs(value)) + (key === 'bytesPerDay' && value < 0 ? ' less' : '');
  if (typeof value === 'string' && /At$|^since$|^endsAt$/.test(key) && !Number.isNaN(Date.parse(value))) return new Date(value).toLocaleDateString(undefined, { dateStyle: 'medium' });
  return String(value);
}

export const severityMeta = {
  critical: { icon: ShieldAlert, className: 'text-offline', soft: 'bg-offline-soft', label: 'Critical' },
  warning: { icon: TriangleAlert, className: 'text-attention', soft: 'bg-attention-soft', label: 'Warning' },
  info: { icon: Info, className: 'text-ink-3', soft: 'bg-sunken', label: 'Note' },
} as const;

/** One open problem: what, since when, the evidence, and the next steps the catalog offers for it. */
export function SignalCard({ signal, runbooks, onRun, canRun, footer }: {
  signal: DeviceSignal; runbooks: RunbookSummary[]; onRun: (runbook: RunbookSummary, reason: string) => void; canRun: boolean; footer?: ReactNode;
}) {
  const m = severityMeta[signal.severity], Icon = m.icon;
  const looks = runbooks.filter(r => r.effect === 'read'), fixes = runbooks.filter(r => r.effect === 'change');
  const evidence = Object.entries(signal.evidence).filter(([, v]) => typeof v === 'number' || typeof v === 'string').slice(0, 4);
  return (
    <div className="rounded-xl border border-line bg-panel p-4 shadow-card">
      <div className="flex items-start gap-3">
        <span className={cn('mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg', m.soft, m.className)}><Icon className="size-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{signal.title}</p>
          <p className="mt-0.5 text-[13.5px] leading-relaxed text-ink-2">{signal.detail}</p>
          <p className="mt-1 text-[12px] text-ink-3" title={absolute(signal.firstSeenAt)}>{m.label} · first seen {ago(signal.firstSeenAt)} · last confirmed {ago(signal.lastSeenAt)}</p>
          {evidence.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {evidence.map(([k, v]) => (
                <span key={k} className="rounded-md bg-sunken px-1.5 py-0.5 text-[11.5px] text-ink-2"><span className="text-ink-3">{evidenceLabel[k] ?? k}</span> {formatEvidence(k, v)}</span>
              ))}
            </div>
          )}
        </div>
      </div>
      {(looks.length > 0 || fixes.length > 0) && (
        <div className="mt-3 border-t border-line pt-3">
          <p className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-ink-3">Suggested next steps</p>
          <div className="flex flex-wrap gap-2">
            {looks.map(r => (
              <button key={r.id} type="button" disabled={!canRun} onClick={() => onRun(r, signal.title)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-[13px] font-medium text-ink-2 transition-colors hover:border-line-strong hover:bg-hover hover:text-ink disabled:opacity-50">
                <Play className="size-3.5 text-series" />Look: {r.title}
              </button>
            ))}
            {fixes.map(r => (
              <button key={r.id} type="button" disabled={!canRun} onClick={() => onRun(r, signal.title)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-brand/30 bg-brand-soft/50 px-2.5 py-1.5 text-[13px] font-medium text-ink transition-colors hover:bg-brand-soft disabled:opacity-50">
                <Wrench className="size-3.5 text-brand" />Fix: {r.title}<span className="text-[11.5px] font-normal text-ink-3">· needs approval</span>
              </button>
            ))}
          </div>
          {/* What may stop a fix working, before anyone approves it (WRMM-173). */}
          {fixes.filter(r => r.caveat).map(r => <p key={`${r.id}-caveat`} data-caveat className="mt-2 text-[12px] text-ink-3">{r.caveat}</p>)}
          {!canRun && <p className="mt-2 text-[12px] text-ink-3">You don’t have permission to run runbooks on this device.</p>}
        </div>
      )}
      {footer}
    </div>
  );
}

function Tile({ icon: Icon, label, value, sub, children, tone }: { icon: typeof Cpu; label: string; value: ReactNode; sub?: ReactNode; children?: ReactNode; tone?: 'warn' | 'critical' }) {
  return (
    <div className="min-w-0 rounded-xl border border-line bg-panel p-3.5 shadow-card">
      <p className="flex items-center gap-1.5 text-[12px] font-medium text-ink-3"><Icon className="size-3.5" />{label}</p>
      <p className={cn('mt-1 text-[22px] font-semibold leading-tight tabular', tone === 'critical' ? 'text-offline' : tone === 'warn' ? 'text-attention' : 'text-ink')}>{value}</p>
      {sub && <p className="truncate text-[12px] text-ink-3">{sub}</p>}
      {children && <div className="mt-2">{children}</div>}
    </div>
  );
}

/** Waiting count shows whenever it is above zero, including when the last install time is unknown (WRMM-175). */
export function updatesDetail(u: DeviceFacts['updates']): ReactNode {
  const waiting = u.pendingCount !== null && u.pendingCount > 0 ? `${u.pendingCount} waiting` : '';
  const installed = u.lastInstalledAt ? `Last installed ${ago(u.lastInstalledAt)}` : '';
  const text = installed && waiting ? `${installed} · ${waiting}` : installed || waiting;
  const pause = updatePause(u);
  return <>
    {pause && <span className="block text-[12px] font-medium text-attention">{pause.text}</span>}
    {text && <span className="block text-[12px] text-ink-3">{text}</span>}
  </>;
}

/**
 * Windows Update paused in Settings or by policy (WRMM-310), quality updates first: they carry the security fixes. Null
 * when not paused, when the device couldn't tell, or when its agent doesn't report a pause.
 */
export function updatePause(u: DeviceFacts['updates']): { text: string; quality: boolean } | null {
  const quality = u.pause?.quality.paused === true;
  const kind = quality ? u.pause!.quality : u.pause?.feature.paused ? u.pause.feature : null;
  if (!kind) return null;
  const label = quality ? 'Updates paused' : 'Feature updates paused';
  return { text: kind.until ? `${label} until ${new Date(kind.until).toLocaleDateString(undefined, { dateStyle: 'medium' })}` : label, quality };
}

/**
 * Whether Windows needs a restart for updates. A required restart comes first; otherwise updates already downloaded but
 * not installed are worth knowing about (WRMM-93), though no restart is required yet.
 */
export function restartStatus(u: DeviceFacts['updates'], now = Date.now()): { text: string; attention: boolean } {
  if (u.pendingReboot) {
    const flow = u.restartFlow ? ` · ${restartFlowText(u.restartFlow)}` : '';
    return { text: `Restart pending${u.pendingRebootSince ? ` for ${duration(now - Date.parse(u.pendingRebootSince))}` : ''}${flow}`, attention: true };
  }
  const restart = u.pendingReboot === false ? 'No restart pending' : 'Restart requirement unknown';
  const downloaded = u.readyCount ? ` · ${u.readyCount} downloaded ${u.readyCount === 1 ? 'update' : 'updates'} not installed yet` : '';
  return { text: restart + downloaded, attention: false };
}

/** A device-local "YYYY-MM-DDTHH:MM" as "Wed 10:00", read as the device's wall clock, not converted. */
function deviceTime(local: string): string {
  const [date = '', time = ''] = local.split('T');
  const [y, m, d] = date.split('-').map(Number);
  const day = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1)).toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
  return `${day} ${time}`;
}

/** The customer restart flow (WRMM-409): which popup is next, or the countdown. */
export function restartFlowText(f: NonNullable<DeviceFacts['updates']['restartFlow']>): string {
  if (f.state === 'restarting') return 'restarting now (the user chose to, or the countdown ended)';
  if (f.state === 'held') return 'the user chose to restart; waiting for the agent update or Windows patch under way';
  if (f.state === 'countdown') {
    const at = f.countdownUntil ? new Date(f.countdownUntil).toLocaleTimeString(undefined, { timeStyle: 'short' }) : 'soon';
    return `restarting at ${at} after the 15-minute countdown`;
  }
  const popup = f.state === 'first' ? 'first popup' : f.state === 'second' ? 'second popup (not now once)'
    : f.state === 'waiting' ? 'waiting for the user: final popup asks only' : 'final popup (not now twice)';
  const due = f.nextPrompt ? `${deviceTime(f.nextPrompt)} device time` : 'when someone is at the computer';
  const mode = f.state === 'first' || f.state === 'second' ? (f.automatic ? ', then restarts automatically' : ', prompt only') : '';
  return `${popup} ${due}${mode}`;
}

export function Vitals({ t }: { t: DeviceTelemetry }) {
  const system = t.volumes.find(v => v.system) ?? t.volumes[0];
  const freePct = system ? (100 * system.freeBytes) / system.totalBytes : null;
  const pct = (v: number | null) => (v === null ? '—' : `${Math.round(v)}%`);
  const uptime = t.facts?.bootAt ? Date.now() - Date.parse(t.facts.bootAt) : null;
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Tile icon={Cpu} label="CPU" value={pct(t.current.cpuPct)} sub={t.facts ? `${t.facts.hardware.cpu.cores === null ? `${t.facts.hardware.cpu.threads} threads` : `${t.facts.hardware.cpu.cores} cores`} · ${t.facts.hardware.cpu.model}` : undefined}
        tone={(t.current.cpuPct ?? 0) >= 90 ? 'warn' : undefined}><Sparkline values={t.metrics.points.map(p => p.cpuPct)} /></Tile>
      <Tile icon={MemoryStick} label="Memory" value={pct(t.current.memoryPct)} sub={t.facts ? `of ${formatBytes(t.facts.hardware.memoryBytes)}` : undefined}
        tone={(t.current.memoryPct ?? 0) >= 90 ? 'warn' : undefined}><Sparkline values={t.metrics.points.map(p => p.memoryPct)} /></Tile>
      <Tile icon={HardDrive} label={system ? `Free on ${system.mount}` : 'Storage'} value={system ? formatBytes(system.freeBytes) : '—'}
        sub={system ? `${freePct!.toFixed(0)}% of ${formatBytes(system.totalBytes)}${system.projection.daysToFull !== null && system.projection.daysToFull <= 30 ? ` · full in ~${Math.max(1, Math.round(system.projection.daysToFull))} days` : ''}` : undefined}
        tone={freePct !== null && freePct < 5 ? 'critical' : freePct !== null && freePct < 15 ? 'warn' : undefined}>
        {system && <UsageBar used={system.totalBytes - system.freeBytes} total={system.totalBytes} tone={freePct! < 5 ? 'critical' : freePct! < 15 ? 'warn' : 'normal'} />}
      </Tile>
      <Tile icon={Activity} label="Up for" value={uptime !== null ? duration(uptime) : '—'} sub={t.facts?.bootAt ? `since ${absolute(t.facts.bootAt)}` : undefined}
        tone={t.facts?.updates.pendingReboot ? 'warn' : undefined}>
        {t.facts?.updates.pendingReboot ? <p className="text-[12px] font-medium text-attention">Restart pending</p>
          : t.facts?.updates.readyCount ? <p className="text-[12px] text-ink-2">Updates downloaded, not installed</p> : null}
      </Tile>
    </div>
  );
}

function Group({ title, icon: Icon, children }: { title: string; icon: typeof Cpu; children: ReactNode }) {
  return (
    <Card className="p-4">
      <h3 className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-ink-2"><Icon className="size-4 text-ink-3" />{title}</h3>
      <dl className="space-y-1.5 text-[13px]">{children}</dl>
    </Card>
  );
}
function Row({ label, children }: { label: string; children: ReactNode }) {
  return <div className="grid grid-cols-[130px_1fr] gap-2"><dt className="text-ink-3">{label}</dt><dd className="min-w-0 break-words text-ink">{children}</dd></div>;
}
const yesNo = (v: boolean | null | undefined, good = true) => v === null || v === undefined
  ? <span className="italic text-ink-3">Not reported</span>
  : <span className={v === good ? 'text-healthy' : 'text-offline'}>{v ? 'On' : 'Off'}</span>;

/** Who is at the computer and how long they have been idle, from the latest metrics (WRMM-159); agents before it say nothing. */
function AtTheComputer({ facts }: { facts: DeviceFacts }) {
  const presence = describePresence(facts.presence, useNow(30_000));
  if (!presence) return null;
  return <Row label="At the computer">{presence.text}{presence.asOf && <span className="text-ink-3"> · as of {presence.asOf}</span>}</Row>;
}

/** `agent`: the device's agent is the newest release (current), an update for it is under way (updating), or unknown. */
export function FactsPanel({ t, update, agent }: { t: DeviceTelemetry; update?: { version: string; open: () => void } | null; agent?: 'current' | 'updating' | 'reinstall-arm64' }) {
  const f = t.facts;
  if (!f) return null;
  const life = t.lifecycle;
  const av = f.security.antivirus;
  const restart = restartStatus(f.updates);
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Group title="Operating system" icon={CalendarClock}>
        <Row label="Version">{f.os.name} {f.os.version}{f.os.build ? <span className="text-ink-3"> · build {f.os.build}</span> : null}</Row>
        {f.os.edition && <Row label="Edition">{f.os.edition}{f.os.architecture ? ` · ${f.os.architecture}` : ''}</Row>}
        {life && (
          <Row label="Security updates">
            <span className={cn('font-medium', life.status === 'unsupported' ? 'text-offline' : life.status === 'ending' ? 'text-attention' : life.status === 'supported' ? 'text-healthy' : 'text-ink-3')}>
              {life.status === 'unsupported' ? 'Ended' : life.status === 'ending' ? 'Ending soon' : life.status === 'supported' ? 'Supported' : 'Not assessed'}
            </span>
            {life.endsAt && <span className="text-ink-3"> · {life.status === 'unsupported' ? 'ended' : 'until'} {new Date(life.endsAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}</span>}
            {life.note && <span className="block text-[12px] text-ink-3">{life.note}</span>}
          </Row>
        )}
        <Row label="Updates"><span className={cn(restart.attention && 'text-attention')}>{restart.text}</span>
          {updatesDetail(f.updates)}</Row>
      </Group>
      <Group title="Protection" icon={ShieldCheck}>
        <Row label="Antivirus">{av === null ? <span className="text-ink-3">Not reported</span> : av.length ? av.map(a => (
          <span key={a.name} className="block">{a.name} · {a.mode === 'passive' ? <span className="text-ink-3">Passive (another antivirus must protect)</span>
            : antivirusProductState(a, f.bootAt, t.collectedAt ? Date.parse(t.collectedAt) : Date.now()) === 'unknown'
              ? <span className="italic text-ink-3">{a.mode === 'initializing' ? 'Initializing · protection unknown' : 'Protection unknown'}</span>
              : <>real-time {yesNo(a.realTimeProtection)}</>}
            {a.signaturesUpdatedAt && <span className="block text-[12px] text-ink-3">Definitions {ago(a.signaturesUpdatedAt)}</span>}</span>
        )) : <span className="text-offline">None reported</span>}</Row>
        <Row label="Firewall">{yesNo(f.security.firewall.enabled)}</Row>
        <Row label="Disk encryption"><span className={f.security.diskEncryption === 'on' ? 'text-healthy' : f.security.diskEncryption === 'unknown' ? 'italic text-ink-3' : 'text-offline'}>
          {{ on: 'On', off: 'Off', partial: 'Partly encrypted', unknown: 'Not reported' }[f.security.diskEncryption]}</span></Row>
        {f.hardware.tpm && <Row label="TPM / Secure Boot">{f.hardware.tpm.present ? `TPM ${f.hardware.tpm.version ?? ''}` : 'No TPM'} · Secure Boot {yesNo(f.hardware.secureBoot)}</Row>}
      </Group>
      <Group title="Hardware" icon={Cpu}>
        <Row label="Model">{[f.hardware.manufacturer, f.hardware.model].filter(Boolean).join(' ') || '—'} <span className="text-ink-3">· {f.hardware.chassis}</span></Row>
        <Row label="Processor">{f.hardware.cpu.model} <span className="text-ink-3">· {f.hardware.cpu.cores === null ? 'Core count not reported' : `${f.hardware.cpu.cores} cores`} / {f.hardware.cpu.threads} threads</span></Row>
        <Row label="Memory">{formatBytes(f.hardware.memoryBytes)}</Row>
        {f.hardware.serial && <Row label="Serial"><span className="font-mono">{f.hardware.serial}</span></Row>}
      </Group>
      <Group title="Network and people" icon={CircleCheck}>
        {f.network.interfaces.map(i => <Row key={i.name} label={i.name}><span className="font-mono">{[...i.ipv4, ...i.ipv6].join(', ') || '—'}</span>{i.mac && <span className="block font-mono text-[12px] text-ink-3">{i.mac}</span>}</Row>)}
        {f.network.domain && <Row label="Domain">{f.network.domain}</Row>}
        <Row label="Signed in">{f.loggedInUsers.length ? f.loggedInUsers.join(', ') : <span className="text-ink-3">Nobody</span>}</Row>
        <AtTheComputer facts={f} />
        <Row label="Agent">{f.agent.version} <span className="text-ink-3">· {f.agent.features?.includes('auto-update') ? (agent === 'reinstall-arm64' ? 'updates held · ' : 'updates itself · ') : ''}inventory {t.collectedAt ? ago(t.collectedAt) : 'never'}</span>
          {update && agent !== 'reinstall-arm64' && <button type="button" onClick={update.open} className="ml-2 inline-flex items-center gap-1 rounded-md bg-brand-soft px-1.5 py-px text-[12px] font-medium text-brand hover:underline">
            <ArrowUpCircle className="size-3.5" />Update to {update.version}</button>}
          {agent === 'reinstall-arm64' && <span className="ml-2 text-[12px] font-medium text-attention">{WINDOWS_ARM_REINSTALL}</span>}
          {!update && agent === 'current' && <span className="ml-2 text-[12px] font-medium text-healthy">Up to date</span>}
          {!update && agent === 'updating' && <span className="ml-2 text-[12px] font-medium text-ink-2">Updating…</span>}</Row>
      </Group>
    </div>
  );
}

export function ResolvedList({ signals }: { signals: DeviceSignal[] }) {
  if (!signals.length) return null;
  return (
    <div>
      <p className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-3">Resolved this week</p>
      <ul className="space-y-1">
        {signals.map(s => (
          <li key={s.id} className="flex items-center gap-2 text-[13px] text-ink-2"><CircleCheck className="size-3.5 text-healthy" />
            <span className="min-w-0 flex-1 truncate">{s.title}</span><span className="shrink-0 text-ink-3">resolved {ago(s.resolvedAt!)}</span></li>
        ))}
      </ul>
    </div>
  );
}

